import { defineStore } from 'pinia';
import type { CartInterface, CartItemInterface } from '~~/server/types/CartInterface';
import { tracking } from '~/services/tracking.client';

const STORAGE_KEY = 'b2c_cart'
const TOKEN_KEY = 'b2c_cart_token'
const cartHeaders = () => ({ 'X-Cart-Token': localStorage.getItem(TOKEN_KEY) || '' })

const saveToStorage = (cart: CartInterface) => {
    localStorage.setItem(STORAGE_KEY, String(cart?.id || 0))
    if (cart.cart_token) localStorage.setItem(TOKEN_KEY, cart.cart_token)
    else localStorage.removeItem(TOKEN_KEY)
}

const getFromStorage = (): number => {
    return Number(localStorage.getItem(STORAGE_KEY) || '0')
}

const removeFromStorage = () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(TOKEN_KEY)
}

const createEmptyCart = (): CartInterface => ({
    id: 0,
    products: [],
})

interface CartState {
    _cart: CartInterface
    _carts: CartInterface[]
    _confirmedItems: Record<number, CartItemInterface>
    _loaded: boolean
    _promise: Promise<void|CartInterface> | null
}

interface changeCountResponse {
    status:string
    message:string;
    title:string;
    count:number;
    cart_item_id:number
}

export const useCartStore = defineStore('cart', {
    state: (): CartState => ({
        _cart: createEmptyCart() as CartInterface,
        _carts: [],
        _loaded: false,
        _confirmedItems: {},
        _promise: null,
    }),

    getters: {
        cart: (state) => state._cart,
        carts: (state) => state._carts,
        isLoaded: (state) => state._loaded,
        total: (state) => state._cart?.products?.reduce((acc, item) => acc + item.total!, 0),
        total_no_vat: (state) => state._cart?.products?.reduce((acc, item) => acc + item.total_no_vat!, 0),
    },

    actions: {
        async loadCart(cart_id:undefined|number=undefined, force=false) {
            if (this._loaded && !force) return
            if (this._promise) {
                await this._promise
                return
            }
            const id = cart_id || getFromStorage()

            if (!id) {
                this._cart = createEmptyCart()
                this._loaded = true
                return
            }

            const { $api } = useNuxtApp()

            this._promise = (async () => {
                    const res = await $api<CartInterface>('/cart/' + id, { method: 'GET', headers: cartHeaders() })
                    this._cart = res?.id ? res : createEmptyCart()
                    this._confirmedItems = Object.fromEntries(this._cart.products.map(item => [item.cart_item_id, { ...item }]));
                    saveToStorage(this._cart);

                this._loaded = true
            })()

            try {
                await this._promise
            } catch (error: any) {
                const status = error?.status ?? error?.response?.status

                if (status===404) {
                    this._cart = createEmptyCart()
                    removeFromStorage();
                } else {
                    throw error;
                }
            } finally {
                this._promise = null
            }
        },
        async clearCart(req:boolean=true) {
            const id = getFromStorage()
            if (!id) {
                this._cart = createEmptyCart()
                removeFromStorage()
                return
            }
            if (req) {
              const { $api } = useNuxtApp();
              await $api<CartInterface>("/cart/" + id, { method: "DELETE", headers: cartHeaders() });
            }
            removeFromStorage()
            this._cart = createEmptyCart()
        },
        async addToCart(cartItem:CartItemInterface, convertToOrder:boolean=false, age18:boolean|null=null):Promise<CartItemInterface> {
            const id = this._cart?.id || getFromStorage();
            const previousCounts = new Map(this._cart.products.map(line => [line.cart_item_id, Number(line.count)]));
            const { $api } = useNuxtApp();
            const res = await $api<CartItemInterface>(`/cart/${id}`, {
                    method: 'PUT',
                headers: cartHeaders(),
                    body: {
                        convertToOrder:convertToOrder,
                        finger_print: cartItem.finger_print,
                        product_id: cartItem.product_id,
                        code: cartItem.code,
                        conversion: cartItem.conversion,
                        count: cartItem.count,
                        age18,
                        options: cartItem.options,
                        options_data: cartItem.options_data,
                        ...(cartItem.subscription_months ? { subscription_months: cartItem.subscription_months } : {}),
                        ...(cartItem.reservation ? { reservation: { starts_at: cartItem.reservation.starts_at } } : {}),
                    }
                });
            if (res?.cart_token) {
                localStorage.setItem(TOKEN_KEY, res.cart_token);
                localStorage.setItem(STORAGE_KEY, String(res.cart_id));
            }
            if (!res?.status) {
                    throw new Error('Cart save failed');
                }
            if (res.status ==='added') {
                this._loaded=false;
                await this.loadCart(res.cart_id);
                saveToStorage(this._cart);
            }
            const index=this._cart.products.findIndex(p=>p.cart_item_id===res.cart_item_id);

            if (res.status ==='updated') {
                this._cart.products[index]=res;
                if (this._cart.preorder || res.product?.is_preorder || this._cart.subscription || res.subscription) {
                    await this.loadCart(id, true);
                }
            }

            const savedItem = this._cart.products.find(item => item.cart_item_id === res.cart_item_id) ?? res;
            this._confirmedItems[savedItem.cart_item_id] = { ...savedItem };
            if (savedItem.product && ['added', 'updated', 'changed'].includes(res.status)) {
                const delta = Number(savedItem.count) - (previousCounts.get(savedItem.cart_item_id) ?? 0);
                if (delta > 0) tracking.addToCart({ ...savedItem, count: delta, total: Number(savedItem.price) * delta });
            }
            return res;
        },
        async changeCount(item:CartItemInterface,):Promise<CartItemInterface> {
            const previous = this._confirmedItems[item.cart_item_id] ?? this._cart.products.find(line => line.cart_item_id === item.cart_item_id)
            const previousCount = Number(previous?.count ?? 0)
            const previousItem = previous ? { ...previous } : undefined
            const id = this._cart?.id || getFromStorage();
            const { $api } = useNuxtApp();
            const res = await $api<CartItemInterface>(`/cart/change-count/${id}`, {
                method: 'PUT',
                headers: cartHeaders(),
                body: {
                    cart_item_id: item.cart_item_id,
                    count: item.count,
                }
            });
            if (!res?.status) {
                throw new Error('Cart save failed');
            }
            const index=this._cart.products.findIndex(p=>p.cart_item_id===item.cart_item_id);

            if (res.status ==='removed') {
                delete this._confirmedItems[item.cart_item_id];
                this._cart.products.splice(index, 1);
                if (previousItem) tracking.removeFromCart(previousItem);
            } else {
                this._cart.products[index]=res;
                this._confirmedItems[item.cart_item_id] = { ...res };
                const delta = Number(res.count) - previousCount;
                if (delta > 0) tracking.addToCart({ ...res, count: delta, total: Number(res.price) * delta });
                else if (delta < 0 && previousItem) tracking.removeFromCart({ ...previousItem, count: -delta, total: Number(previousItem.price) * -delta });
            }
            if (this._cart.preorder || item.product?.is_preorder || this._cart.subscription || item.subscription) {
                await this.loadCart(id, true);
            }
            return res;
        },
        async removeItem(cart:CartItemInterface):Promise<CartItemInterface> {
            return await this.changeCount({...cart, count: 0});
        },
        setItemError(item_id:number, error:string){
            this._cart.products = this._cart.products.map(p => p.cart_item_id === item_id ? {...p, error: error} : p);
        },
        async applyDiscount(discount_code:string) {
          const { $api } = useNuxtApp();
          this._cart=await $api<CartInterface>("/cart/apply-discount", {
            method: "POST",
            headers: cartHeaders(),
            body: {
              id: this._cart?.id,
              discount_code: discount_code,
            },
          });
        },

        async removeDiscount() {
            const { $api } = useNuxtApp();
            this._cart=await $api<CartInterface>("/cart/remove-discount/"+this._cart!.id, {
                method: "DELETE",
                headers: cartHeaders(),
            });
        },


    },
})
